import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "~/components/content/content-item-render/hoc";
import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeSuperSplashLargePrimaryContentItemContent$key } from "~/queries/__generated__/homeSuperSplashLargePrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  CoverImageStyledEntityLink,
  Headline,
  HeadlineContainer,
  LinkHighlightArea,
  LiveTagContainer,
  StyledEntityLink,
  SubContentInDesktop,
  SubHeadline,
} from "./styles";

export type Props = {
  children?: ReactNode;
  reference: homeSuperSplashLargePrimaryContentItemContent$key;
} & ContentItemRenderVariantProps;

const Component = forwardRef<HTMLDivElement, Props>(
  ({ children, className, onClick, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeSuperSplashLargePrimaryContentItemContent on Content {
          entityId
          ...entityLink
          ...hooksContentItemProvidersContent
            @arguments(
              withCoverImage: true
              withCoverImageSize540x360: true
              withCoverImageSize1200x800: true
              withHeadline: true
              withLiveTag: true
              withSubHeadline: true
              withActionBar: true
            )
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);

    return (
      <Container className={className} ref={reference}>
        <CoverImageStyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
          <CoverImage>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              },
              withCaption: true,
            })}
          </CoverImage>
        </CoverImageStyledEntityLink>
        <Content>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <LinkHighlightArea>
              <HeadlineContainer>
                <Headline>
                  {providers.headline({ hideTooltip: true, preferSocialHeadline: true })}
                </Headline>
                <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>
              </HeadlineContainer>
              <SubHeadline>{providers.subHeadline()}</SubHeadline>
            </LinkHighlightArea>
          </StyledEntityLink>
          <ActionBar>
            {providers.actionBar({
              commentVariant: "reverse",
              isBookmarkVisible: false,
              isCommentsVisible: true,
            })}
          </ActionBar>
          <SubContentInDesktop>{children}</SubContentInDesktop>
        </Content>
      </Container>
    );
  },
);

Component.displayName = "ContentItemHomeSuperSplashLargePrimary";

export const ContentItemHomeSuperSplashLargePrimary = withHighlightedHeadline(Component);
